import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { createPermissionConfiguration } from '../../services/hrPolicyConfigurationService';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'PermissionConfigurationCreate'>;

export function PermissionConfigurationCreateScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [form, setForm] = useState({
    permissionTypeCode: '',
    permissionTypeName: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (key: keyof typeof form, value: string) =>
    setForm(current => ({ ...current, [key]: value }));

  async function submit() {
    if (!form.permissionTypeCode.trim() || !form.permissionTypeName.trim()) {
      setError('Permission type code and name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runOperation('add', () => createPermissionConfiguration({
        permissionTypeCode: form.permissionTypeCode.trim().toUpperCase(),
        permissionTypeName: form.permissionTypeName.trim(),
        description: form.description.trim() || null,
      }));
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader title="Create permission type" onBack={navigation.goBack} />
        <View style={styles.body}>
          <Text style={[styles.caption, { color: palette.textMuted }]}>
            A permission type is only a category. Monthly count and duration come from the
            overall permission policy.
          </Text>
          <AppInput
            label="Permission type code"
            value={form.permissionTypeCode}
            onChangeText={value => set('permissionTypeCode', value)}
            autoCapitalize="characters"
          />
          <AppInput
            label="Permission type name"
            value={form.permissionTypeName}
            onChangeText={value => set('permissionTypeName', value)}
          />
          <AppInput
            label="Description"
            value={form.description}
            onChangeText={value => set('description', value)}
          />
          {error ? (
            <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
              {error}
            </Text>
          ) : null}
          <AppButton
            title="Create permission type"
            onPress={submit}
            loading={saving}
          />
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  caption: typography.caption,
  error: typography.caption,
});
